import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import {
  Truck, Wrench, ClipboardList, AlertTriangle, Package,
  Gauge, Activity, MapPin, ArrowRight, CheckCircle2,
  Bell, FileText, Boxes, Zap, LineChart, ShieldCheck,
  Smartphone, Calendar, X, Menu,
} from "lucide-react";

// ---------- Reusable primitives ----------
const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

function SectionEyebrow({ children }) {
  return (
    <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-blue-400">/// {children}</div>
  );
}

function BrowserFrame({ children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-white/10 bg-[#0e0e12] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7),0_0_60px_-10px_rgba(0,87,184,0.25)] overflow-hidden ${className}`}>
      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/5 bg-black/40">
        <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
        <div className="ml-3 text-[10px] font-mono text-white/40">rep-fleet.com/app</div>
      </div>
      {children}
    </div>
  );
}

function GlassCard({ children, className = "" }) {
  return (
    <div className={`backdrop-blur-xl bg-white/[0.04] border border-white/10 rounded-xl ${className}`}>
      {children}
    </div>
  );
}

// ---------- Nav ----------
function Nav({ ctaHref, ctaLabel }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const links = [
    { href: "#product", label: "Product" },
    { href: "#features", label: "Features" },
    { href: "#integrations", label: "Integrations" },
    { href: "#how", label: "How It Works" },
    { href: "#contact", label: "Contact" },
  ];
  return (
    <nav
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${scrolled ? "bg-black/60 backdrop-blur-xl border-b border-white/10" : "bg-transparent"}`}
      data-testid="mkt-nav"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          <img src="/logo.png" alt="Rep Fleet" className="w-8 h-8 rounded bg-white/95 p-0.5 object-contain" />
          <span className="font-display font-black tracking-tight text-white text-[15px]">REP FLEET</span>
        </Link>
        <div className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="text-[13px] text-white/70 hover:text-white transition-colors">{l.label}</a>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/login"
            className="text-[13px] text-white/80 hover:text-white transition-colors"
            data-testid="nav-login"
          >
            Login
          </Link>
          <a
            href="#contact"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold bg-[#0057B8] hover:bg-[#0064d1] transition-colors text-white shadow-[0_0_24px_rgba(0,87,184,0.4)]"
            data-testid="nav-cta-demo"
          >
            Request Demo <ArrowRight size={13} />
          </a>
          <button onClick={() => setOpen(!open)} className="lg:hidden text-white/70" data-testid="nav-mobile-toggle">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-black/95 backdrop-blur-xl border-b border-white/10 px-6 py-4 space-y-3">
          {links.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="block text-sm text-white/70 hover:text-white">{l.label}</a>
          ))}
          <Link to="/login" onClick={() => setOpen(false)} className="block text-sm text-white/70 hover:text-white" data-testid="nav-mobile-login">Login</Link>
        </div>
      )}
    </nav>
  );
}

// ---------- Dashboard mockups (in-page rendered) ----------
function DashboardMock() {
  return (
    <div className="p-5 bg-gradient-to-br from-[#0f0f14] to-[#080810]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-blue-400">/// Control Room</div>
          <div className="font-display font-black text-lg text-white leading-tight">Fleet Dashboard</div>
        </div>
        <div className="flex gap-2">
          <span className="text-[10px] font-mono px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">LIVE</span>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3 mb-4">
        {[
          { label: "TOTAL TRUCKS", value: "172", sub: "172 active", tint: "text-white" },
          { label: "TRAILERS", value: "48", sub: "12 reefers", tint: "text-white" },
          { label: "OVERDUE", value: "3", sub: "This week", tint: "text-red-400" },
          { label: "REPAIR COST", value: "$49.1k", sub: "102 records", tint: "text-white" },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
            <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">{s.label}</div>
            <div className={`font-display font-black text-xl mt-1 ${s.tint}`}>{s.value}</div>
            <div className="font-mono text-[10px] text-white/40">{s.sub}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2 rounded-lg border border-white/10 bg-white/[0.02] p-4">
          <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-2">/// Trend · Repair cost by month</div>
          <div className="flex items-end gap-1.5 h-24">
            {[45, 62, 30, 55, 78, 40, 68, 90, 55, 72, 88, 60].map((h, i) => (
              <div key={i} className="flex-1 bg-gradient-to-t from-[#0057B8] to-[#4d8fd6] rounded-t" style={{ height: `${h}%` }} />
            ))}
          </div>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
          <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-3">/// Service queue</div>
          <div className="space-y-2">
            {[
              { unit: "MOT-101", svc: "PM Service", when: "2d", tone: "text-amber-400" },
              { unit: "MOT-104", svc: "Annual", when: "5d", tone: "text-emerald-400" },
              { unit: "MOT-127", svc: "Brakes", when: "OVERDUE", tone: "text-red-400" },
            ].map((r) => (
              <div key={r.unit} className="flex items-center justify-between text-[11px]">
                <span className="font-mono text-white/70">{r.unit}</span>
                <span className="text-white/50">{r.svc}</span>
                <span className={`font-mono font-bold ${r.tone}`}>{r.when}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function WorkOrderMock() {
  return (
    <div className="p-5 bg-gradient-to-br from-[#0f0f14] to-[#080810]">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-blue-400">/// Work Order · WO-2481</div>
          <div className="font-display font-black text-lg text-white">Brake chamber replacement</div>
        </div>
        <span className="text-[10px] font-mono px-2 py-1 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">IN PROGRESS</span>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
          <div className="font-mono text-[9px] uppercase text-white/40">Truck</div>
          <div className="font-bold text-white mt-1">MOT-127 · Kenworth T680</div>
          <div className="text-[11px] text-white/50">697,855 mi · Driver: J. Randhawa</div>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
          <div className="font-mono text-[9px] uppercase text-white/40">Mechanic</div>
          <div className="font-bold text-white mt-1">Sam Cortez</div>
          <div className="text-[11px] text-white/50">Started 8:14 AM · In-house</div>
        </div>
      </div>
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
        <div className="font-mono text-[9px] uppercase text-white/40 mb-2">Parts used</div>
        {[
          { p: "Brake chamber 30/30", q: "2", c: "$186.00" },
          { p: "Air brake fittings", q: "4", c: "$18.40" },
          { p: "Silicone grease", q: "1", c: "$6.90" },
        ].map((r) => (
          <div key={r.p} className="flex justify-between text-[11px] py-1 border-t border-white/5 first:border-0">
            <span className="text-white/80">{r.p}</span>
            <span className="text-white/50 font-mono">×{r.q}</span>
            <span className="text-emerald-400 font-mono">{r.c}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function InventoryMock() {
  return (
    <div className="p-5 bg-gradient-to-br from-[#0f0f14] to-[#080810]">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-blue-400">/// Parts Inventory</div>
          <div className="font-display font-black text-lg text-white">Stockroom</div>
        </div>
        <div className="flex gap-2 text-[10px] font-mono">
          <span className="px-2 py-1 rounded bg-white/5 text-white/60 border border-white/10">141 SKUs</span>
          <span className="px-2 py-1 rounded bg-red-500/10 text-red-400 border border-red-500/20">17 LOW STOCK</span>
        </div>
      </div>
      <div className="space-y-2">
        {[
          { p: "Engine Oil 15W-40", sku: "OIL-1540", qty: 46, unit: "$12.50", tone: "text-emerald-400" },
          { p: "Air Filter · Donaldson", sku: "AF-D22", qty: 12, unit: "$28.00", tone: "text-emerald-400" },
          { p: "Brake Chamber 30/30", sku: "BC-3030", qty: 6, unit: "$93.00", tone: "text-amber-400" },
          { p: "DEF Filter", sku: "DEF-01", qty: 2, unit: "$44.00", tone: "text-red-400" },
        ].map((r) => (
          <div key={r.sku} className="flex items-center gap-3 p-2.5 rounded-lg border border-white/10 bg-white/[0.02]">
            <div className="w-8 h-8 rounded bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
              <Boxes size={16} className="text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-white truncate">{r.p}</div>
              <div className="font-mono text-[10px] text-white/40">{r.sku}</div>
            </div>
            <div className={`font-mono font-bold text-sm ${r.tone}`}>×{r.qty}</div>
            <div className="font-mono text-[11px] text-white/50 w-16 text-right">{r.unit}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VehicleProfileMock() {
  return (
    <div className="p-5 bg-gradient-to-br from-[#0f0f14] to-[#080810]">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-blue-400">/// Unit MOT-127</div>
          <div className="font-display font-black text-xl text-white">Kenworth T680 · 2023</div>
          <div className="text-[11px] text-white/50 mt-1">VIN: 1XKYD40X5NJ523812 · Driver: J. Randhawa</div>
        </div>
        <span className="text-[10px] font-mono px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">ACTIVE</span>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { l: "Odometer", v: "697,855", u: "mi" },
          { l: "Fuel", v: "68", u: "%" },
          { l: "Next PM", v: "2,180", u: "mi" },
        ].map((s) => (
          <div key={s.l} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
            <div className="font-mono text-[9px] uppercase text-white/40">{s.l}</div>
            <div className="font-display font-black text-lg text-white mt-1">
              {s.v}<span className="text-white/40 text-xs font-mono ml-1">{s.u}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1">Maintenance timeline</div>
        {[
          { d: "Aug 3", t: "PM Service · completed", tone: "bg-emerald-500" },
          { d: "Jul 28", t: "Driver reported brake noise", tone: "bg-amber-500" },
          { d: "Jul 28", t: "Work Order WO-2481 opened", tone: "bg-blue-500" },
          { d: "Jun 12", t: "Annual Safety Inspection", tone: "bg-emerald-500" },
        ].map((r, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${r.tone}`} />
            <div className="text-[11px] font-mono text-white/50 w-12">{r.d}</div>
            <div className="text-[12px] text-white/80 flex-1">{r.t}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Floating notification card used behind the hero.
function FloatCard({ icon: Icon, title, sub, tone = "blue", className = "", delay = 0 }) {
  const toneMap = {
    blue: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/20",
    red: "text-red-400 bg-red-500/10 border-red-500/20",
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`backdrop-blur-xl bg-black/60 border border-white/10 rounded-xl p-3 shadow-[0_20px_60px_-10px_rgba(0,0,0,0.8)] ${className}`}
    >
      <div className="flex items-start gap-2.5">
        <div className={`w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0 ${toneMap[tone]}`}>
          <Icon size={15} />
        </div>
        <div className="min-w-0">
          <div className="text-[12px] font-bold text-white leading-tight">{title}</div>
          <div className="text-[10px] text-white/50 font-mono mt-0.5">{sub}</div>
        </div>
      </div>
    </motion.div>
  );
}

// ---------- Sections ----------
function HeroSection({ ctaHref }) {
  return (
    <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden">
      {/* Background texture — subtle grid + radial glow */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full bg-[#0057B8]/15 blur-[120px]" />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <motion.div {...fadeUp}>
            <SectionEyebrow>Fleet Maintenance OS</SectionEyebrow>
            <h1 className="mt-4 font-display font-black text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-white">
              Fleet Maintenance.
              <br />
              <span className="text-white/40">Finally</span> Under Control.
            </h1>
            <p className="mt-6 text-lg text-white/60 leading-relaxed max-w-lg">
              REP Fleet gives trucking companies one place to manage maintenance, inspections, driver-reported issues, work orders, inventory and complete service history.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#contact" data-testid="hero-cta-demo" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0057B8] hover:bg-[#0064d1] text-white font-semibold text-sm transition-colors shadow-[0_0_40px_rgba(0,87,184,0.4)]">
                Request a Demo <ArrowRight size={15} />
              </a>
              <a href="#product" data-testid="hero-cta-explore" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold text-sm transition-colors">
                Explore REP Fleet
              </a>
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-7 relative">
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <BrowserFrame><DashboardMock /></BrowserFrame>

            {/* Floating cards */}
            <div className="absolute -left-4 top-14 w-52 hidden md:block">
              <FloatCard icon={Wrench} title="Service Due Soon" sub="MOT-127 · Brakes · 5d" tone="amber" delay={0.4} />
            </div>
            <div className="absolute -right-6 top-32 w-56 hidden md:block">
              <FloatCard icon={AlertTriangle} title="Driver Issue Reported" sub="MOT-104 · Coolant leak" tone="red" delay={0.6} />
            </div>
            <div className="absolute -left-8 bottom-24 w-52 hidden md:block">
              <FloatCard icon={ClipboardList} title="Work Order Created" sub="WO-2481 · PM Service" tone="blue" delay={0.8} />
            </div>
            <div className="absolute -right-4 bottom-8 w-52 hidden md:block">
              <FloatCard icon={CheckCircle2} title="PM Completed" sub="MOT-101 · 6 min ago" tone="emerald" delay={1.0} />
            </div>
            <div className="absolute -left-2 -bottom-2 w-56 hidden lg:block">
              <FloatCard icon={Package} title="Part Inventory Low" sub="DEF Filter · 2 left" tone="red" delay={1.2} />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ProductOverviewSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0.1, 0.5], [0.92, 1.02]);
  const labels = [
    { text: "Fleet Overview", top: "18%", left: "6%" },
    { text: "Upcoming Services", top: "58%", right: "4%" },
    { text: "Open Issues", top: "28%", right: "10%" },
    { text: "Work Orders", bottom: "18%", left: "8%" },
    { text: "Maintenance Costs", bottom: "34%", left: "48%" },
  ];
  return (
    <section id="product" ref={ref} className="py-32 border-t border-white/5 relative">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
        <motion.div {...fadeUp}>
          <SectionEyebrow>The Platform</SectionEyebrow>
          <h2 className="mt-4 font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tight text-white leading-tight">
            Your entire maintenance operation.<br /><span className="text-white/40">One platform.</span>
          </h2>
        </motion.div>

        <motion.div style={{ scale }} className="mt-16 relative max-w-5xl mx-auto">
          <BrowserFrame><DashboardMock /></BrowserFrame>
          {labels.map((l, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 + i * 0.1 }}
              className="absolute hidden md:block"
              style={{ top: l.top, bottom: l.bottom, left: l.left, right: l.right }}
            >
              <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400/80 bg-black/60 backdrop-blur-md border border-white/10 rounded-full px-3 py-1">
                {l.text}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function DriverReportingSection() {
  return (
    <section className="py-32 border-t border-white/5 relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 relative">
          <motion.div {...fadeUp} className="relative mx-auto w-[280px]">
            {/* Phone frame */}
            <div className="rounded-[2.5rem] border-[10px] border-black bg-black shadow-[0_30px_80px_-10px_rgba(0,0,0,0.9),0_0_60px_-10px_rgba(0,87,184,0.3)]">
              <div className="rounded-[1.6rem] overflow-hidden bg-gradient-to-br from-[#0f0f14] to-[#080810]">
                <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
                  <div>
                    <div className="font-mono text-[9px] text-blue-400">/// DRIVER</div>
                    <div className="text-white font-bold text-sm">Report an issue</div>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                    <Truck size={14} className="text-blue-400" />
                  </div>
                </div>
                <div className="p-4 space-y-3">
                  <div>
                    <div className="text-[10px] font-mono text-white/40 mb-1">UNIT</div>
                    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-white text-sm">MOT-127</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono text-white/40 mb-1">CATEGORY</div>
                    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-white text-sm">Brakes</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono text-white/40 mb-1">DESCRIPTION</div>
                    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-white text-xs leading-relaxed">Grinding noise when braking downhill on I-81 near Roanoke.</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-[10px] font-mono text-white/60 flex-1">+ 2 photos</div>
                    <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-2 py-2 text-[10px] font-mono text-red-400">HIGH</div>
                  </div>
                  <button className="w-full rounded-lg bg-[#0057B8] text-white text-sm font-semibold py-2.5">Submit Issue</button>
                </div>
              </div>
            </div>
            {/* floating flow indicator */}
            <div className="absolute -right-6 top-1/2 -translate-y-1/2 hidden lg:flex items-center gap-1">
              <motion.div
                animate={{ x: [0, 12, 0], opacity: [0.4, 1, 0.4] }}
                transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                className="w-2 h-2 rounded-full bg-blue-400"
              />
              <div className="w-16 h-px bg-gradient-to-r from-blue-400/50 to-transparent" />
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-7">
          <motion.div {...fadeUp}>
            <SectionEyebrow>Driver Reporting Portal</SectionEyebrow>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tight text-white leading-tight">
              Issues reported before they become <span className="text-[#0057B8]">expensive repairs.</span>
            </h2>
            <p className="mt-5 text-white/60 text-lg max-w-xl leading-relaxed">
              Drivers report vehicle problems from their phone. The maintenance team sees it in real time — with unit, category, description, photos, and priority.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              {[
                { l: "Driver", i: Smartphone },
                { l: "REP Fleet", i: Zap },
                { l: "Shop", i: Wrench },
              ].map((n, i) => (
                <React.Fragment key={n.l}>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-xl border border-white/10 bg-white/[0.03] flex items-center justify-center mx-auto">
                      <n.i size={20} className="text-blue-400" />
                    </div>
                    <div className="font-mono text-[10px] uppercase text-white/50 mt-2">{n.l}</div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function WorkOrdersSection() {
  const steps = ["Issue Reported", "Work Order", "Mechanic", "Parts", "Completed", "Service History"];
  return (
    <section className="py-32 border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <motion.div {...fadeUp}>
            <SectionEyebrow>Work Orders</SectionEyebrow>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tight text-white leading-tight">
              From reported issue to <span className="text-[#0057B8]">completed repair.</span>
            </h2>
            <p className="mt-5 text-white/60 text-lg leading-relaxed">
              Every work order captures parts used, labor time, mechanic, and cost — automatically deducting parts from your stockroom.
            </p>
          </motion.div>
        </div>
        <div className="lg:col-span-7">
          <motion.div {...fadeUp}>
            <BrowserFrame><WorkOrderMock /></BrowserFrame>
          </motion.div>
        </div>
      </div>

      {/* Workflow line */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-16">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {steps.map((s, i) => (
              <div key={s} className="relative">
                <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-3 text-center">
                  <div className="font-mono text-[9px] uppercase tracking-widest text-blue-400">Step {i + 1}</div>
                  <div className="text-white text-[13px] font-semibold mt-1">{s}</div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function PreventiveMaintenanceSection() {
  const items = [
    { unit: "MOT-101", type: "Oil Change", when: "in 4 days", tone: "amber" },
    { unit: "MOT-104", type: "PM Service", when: "in 12 days", tone: "emerald" },
    { unit: "MOT-127", type: "Annual Inspection", when: "OVERDUE 3d", tone: "red" },
    { unit: "TRL-88", type: "Trailer Service", when: "in 21 days", tone: "emerald" },
    { unit: "MOT-102", type: "Brake Inspection", when: "in 7 days", tone: "amber" },
    { unit: "MOT-103", type: "DOT Inspection", when: "in 30 days", tone: "emerald" },
  ];
  const toneMap = {
    emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    amber: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    red: "bg-red-500/10 text-red-400 border-red-500/20",
  };
  return (
    <section className="py-32 border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto">
          <SectionEyebrow>Preventive Maintenance</SectionEyebrow>
          <h2 className="mt-4 font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tight text-white leading-tight">
            Know what's due <span className="text-white/40">before</span> it's overdue.
          </h2>
        </motion.div>
        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it, i) => (
            <motion.div
              key={it.unit + it.type}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-5"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono text-[10px] uppercase text-blue-400">{it.unit}</div>
                <div className={`text-[10px] font-mono px-2 py-1 rounded border ${toneMap[it.tone]}`}>
                  {it.tone === "red" ? "OVERDUE" : it.tone === "amber" ? "DUE SOON" : "SCHEDULED"}
                </div>
              </div>
              <div className="font-display font-bold text-xl text-white">{it.type}</div>
              <div className="mt-2 text-white/50 text-sm">{it.when}</div>
              <div className="mt-4 h-1 rounded-full bg-white/5 overflow-hidden">
                <div className={`h-full ${it.tone === "red" ? "bg-red-500 w-full" : it.tone === "amber" ? "bg-amber-500 w-3/4" : "bg-emerald-500 w-1/3"}`} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function IntegrationsSection() {
  return (
    <section id="integrations" className="py-32 border-t border-white/5 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full bg-[#0057B8]/10 blur-[100px] -z-10" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto">
          <SectionEyebrow>Integrations</SectionEyebrow>
          <h2 className="mt-4 font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tight text-white leading-tight">
            Your fleet data <span className="text-white/40">stays</span> in sync.
          </h2>
          <p className="mt-6 text-white/60 text-lg">
            Supported telematics providers push live odometer, fuel, and driver data straight into REP Fleet — so maintenance schedules stay current without manual entry.
          </p>
        </motion.div>

        <div className="mt-20 relative max-w-4xl mx-auto">
          <svg viewBox="0 0 800 300" className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id="line-motive" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#0057B8" stopOpacity="0" />
                <stop offset="50%" stopColor="#0057B8" stopOpacity="1" />
                <stop offset="100%" stopColor="#0057B8" stopOpacity="0" />
              </linearGradient>
            </defs>
            <motion.path
              d="M 100 150 Q 250 150 400 150"
              stroke="url(#line-motive)"
              strokeWidth="2"
              fill="none"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, delay: 0.3 }}
            />
            <motion.path
              d="M 700 150 Q 550 150 400 150"
              stroke="url(#line-motive)"
              strokeWidth="2"
              fill="none"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, delay: 0.5 }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-between px-4 md:px-8">
            <GlassCard className="p-4 md:p-6 text-center w-28 md:w-40">
              <div className="font-display font-black text-lg md:text-xl text-white">Motive</div>
              <div className="mt-2 text-[10px] font-mono uppercase text-emerald-400">CONNECTED</div>
            </GlassCard>
            <div className="rounded-2xl border border-white/20 bg-black/80 backdrop-blur-xl px-6 md:px-10 py-6 md:py-8 text-center shadow-[0_0_60px_rgba(0,87,184,0.4)]">
              <img src="/logo.png" alt="Rep Fleet" className="w-10 h-10 mx-auto rounded bg-white/95 p-0.5 object-contain" />
              <div className="font-display font-black text-xl md:text-2xl text-white mt-2">REP FLEET</div>
              <div className="text-[10px] font-mono text-blue-400 mt-1">MAINTENANCE ENGINE</div>
            </div>
            <GlassCard className="p-4 md:p-6 text-center w-28 md:w-40">
              <div className="font-display font-black text-lg md:text-xl text-white">Samsara</div>
              <div className="mt-2 text-[10px] font-mono uppercase text-amber-400">COMING SOON</div>
            </GlassCard>
          </div>
        </div>

        <div className="mt-16 flex flex-col md:flex-row justify-center items-center gap-2 md:gap-4 text-sm text-white/60 font-mono">
          <span>Live Odometer Data</span><ArrowRight size={14} />
          <span>Maintenance Engine</span><ArrowRight size={14} />
          <span className="text-blue-400">Service Due Alert</span>
        </div>
      </div>
    </section>
  );
}

function InventorySection() {
  return (
    <section className="py-32 border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <motion.div {...fadeUp}>
            <SectionEyebrow>Shop Inventory</SectionEyebrow>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tight text-white leading-tight">
              Know what's <span className="text-[#0057B8]">on the shelf.</span>
            </h2>
            <p className="mt-5 text-white/60 text-lg leading-relaxed">
              Parts auto-deduct when used on a work order. Low-stock alerts fire the moment you cross a threshold you set.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
              {[
                { p: "Oil Filters", q: "18", tone: "text-emerald-400" },
                { p: "Brake Chambers", q: "6", tone: "text-amber-400" },
                { p: "Air Filters", q: "12", tone: "text-emerald-400" },
                { p: "DEF Filters", q: "LOW", tone: "text-red-400" },
              ].map((r) => (
                <div key={r.p} className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                  <div className="text-[11px] text-white/60 font-mono">{r.p}</div>
                  <div className={`font-display font-black text-2xl mt-1 ${r.tone}`}>{r.q}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
        <div className="lg:col-span-6">
          <motion.div {...fadeUp}>
            <BrowserFrame><InventoryMock /></BrowserFrame>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function VehicleHistorySection() {
  return (
    <section className="py-32 border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto">
          <SectionEyebrow>Complete Vehicle History</SectionEyebrow>
          <h2 className="mt-4 font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tight text-white leading-tight">
            Every repair. Every service. <span className="text-white/40">Every unit.</span>
          </h2>
        </motion.div>
        <motion.div {...fadeUp} className="mt-16 max-w-4xl mx-auto">
          <BrowserFrame><VehicleProfileMock /></BrowserFrame>
        </motion.div>
      </div>
    </section>
  );
}

function FeatureShowcaseSection() {
  const features = [
    { label: "Driver Reporting Portal", title: "The shop knows before the truck breaks.", desc: "Drivers report issues from the road with photos and priority." },
    { label: "Work Orders", title: "Digital work orders end to end.", desc: "Parts, labor, and cost tracked automatically per repair." },
    { label: "Preventive Maintenance", title: "PMs that never slip.", desc: "Time and mileage-based schedules alert you before due dates." },
    { label: "Service Reminders", title: "Alerts that command action.", desc: "Overdue and upcoming rolled into one queue." },
    { label: "Vehicle Maintenance History", title: "A full record per truck.", desc: "See every repair, service and driver report on one timeline." },
    { label: "Shop Inventory", title: "Stockroom in sync with the shop.", desc: "Auto-deduct on repairs. Low-stock alerts. Parts value visible." },
    { label: "Fleet Dashboard", title: "The whole fleet at a glance.", desc: "KPI cards, cost trends, overdue services in one control room." },
    { label: "Motive Integration", title: "Live odometer, live fuel.", desc: "Maintenance triggers stay accurate without manual entry." },
    { label: "Asset Management", title: "Trucks, trailers, all of it.", desc: "Unified profiles including reefers, dry vans and heavy haul." },
  ];
  return (
    <section id="features" className="py-32 border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto mb-20">
          <SectionEyebrow>Feature Showcase</SectionEyebrow>
          <h2 className="mt-4 font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tight text-white leading-tight">
            Built for the whole maintenance operation.
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f, i) => (
            <motion.div
              key={f.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
              className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 hover:bg-white/[0.04] hover:border-white/20 transition-colors group"
            >
              <div className="font-mono text-[10px] uppercase tracking-widest text-blue-400">/// {f.label}</div>
              <div className="mt-3 font-display font-black text-2xl text-white leading-tight">{f.title}</div>
              <div className="mt-3 text-white/50 text-sm leading-relaxed">{f.desc}</div>
              <div className="mt-6 h-24 rounded-lg border border-white/5 bg-gradient-to-br from-white/[0.03] to-transparent flex items-center justify-center">
                <div className="w-14 h-14 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  {React.createElement([Smartphone, ClipboardList, Calendar, Bell, FileText, Boxes, LineChart, Zap, ShieldCheck][i] || Zap, { size: 24, className: "text-blue-400" })}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function InteractiveFleetSection() {
  const [active, setActive] = useState(null);
  const trucks = [
    { id: "105", km: "387,420", pm: "4,580 KM", issues: 1, status: "ACTIVE", top: "22%", left: "18%" },
    { id: "127", km: "697,855", pm: "2,180 KM", issues: 2, status: "IN SHOP", top: "58%", left: "42%" },
    { id: "104", km: "510,013", pm: "8,900 KM", issues: 0, status: "ACTIVE", top: "34%", left: "68%" },
    { id: "101", km: "483,844", pm: "12,400 KM", issues: 0, status: "ACTIVE", top: "70%", left: "76%" },
  ];
  return (
    <section id="how" className="py-32 border-t border-white/5 relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto">
          <SectionEyebrow>Fleet Yard</SectionEyebrow>
          <h2 className="mt-4 font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tight text-white leading-tight">
            Every truck. <span className="text-white/40">One glance.</span>
          </h2>
          <p className="mt-6 text-white/60 text-lg">Hover any unit to see its live maintenance status.</p>
        </motion.div>

        <div className="mt-16 relative aspect-[16/9] rounded-2xl border border-white/10 overflow-hidden bg-gradient-to-br from-[#0a0a0e] to-[#050508]">
          {/* Grid floor */}
          <div className="absolute inset-0"
            style={{
              backgroundImage: "linear-gradient(rgba(0,87,184,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(0,87,184,0.08) 1px, transparent 1px)",
              backgroundSize: "50px 50px",
              transform: "perspective(1000px) rotateX(45deg) scale(1.5)",
              transformOrigin: "center top",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black" />

          {trucks.map((t) => (
            <button
              key={t.id}
              onMouseEnter={() => setActive(t.id)}
              onMouseLeave={() => setActive(null)}
              className="absolute group"
              style={{ top: t.top, left: t.left }}
              data-testid={`fleet-truck-${t.id}`}
            >
              <div className="relative">
                <div className={`w-14 h-8 rounded-md border-2 flex items-center justify-center transition-all ${active === t.id ? "border-blue-400 bg-blue-500/20 scale-110" : "border-white/20 bg-white/5"}`}>
                  <Truck size={16} className="text-white" />
                </div>
                <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-8 rounded-md ${t.issues > 0 ? "shadow-[0_0_30px_rgba(239,68,68,0.5)]" : "shadow-[0_0_20px_rgba(0,87,184,0.4)]"} pointer-events-none`} />
              </div>
              <AnimatePresence>
                {active === t.id && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="absolute left-1/2 -translate-x-1/2 mt-3 w-48 backdrop-blur-xl bg-black/90 border border-white/20 rounded-xl p-4 shadow-2xl z-10"
                  >
                    <div className="font-mono text-[10px] uppercase text-blue-400">/// UNIT</div>
                    <div className="font-display font-black text-xl text-white">{t.id}</div>
                    <div className="mt-2 space-y-1 text-[11px]">
                      <div className="flex justify-between"><span className="text-white/50">Odometer</span><span className="font-mono text-white">{t.km}</span></div>
                      <div className="flex justify-between"><span className="text-white/50">Next PM</span><span className="font-mono text-white">{t.pm}</span></div>
                      <div className="flex justify-between"><span className="text-white/50">Open issues</span><span className={`font-mono font-bold ${t.issues > 0 ? "text-red-400" : "text-emerald-400"}`}>{t.issues}</span></div>
                      <div className="flex justify-between"><span className="text-white/50">Status</span><span className={`font-mono font-bold ${t.status === "ACTIVE" ? "text-emerald-400" : "text-amber-400"}`}>{t.status}</span></div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function RoiSection() {
  const without = ["Missed PMs", "Paper records", "Unreported issues", "Unexpected downtime", "Disconnected maintenance information"];
  const withRep = ["Maintenance visibility", "Driver reporting", "Digital work orders", "Service reminders", "Centralized vehicle history", "Inventory visibility"];
  return (
    <section className="py-32 border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto">
          <SectionEyebrow>ROI</SectionEyebrow>
          <h2 className="mt-4 font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tight text-white leading-tight">
            Maintenance shouldn't start<br/>when the <span className="text-red-400">truck breaks down.</span>
          </h2>
        </motion.div>
        <div className="mt-16 grid md:grid-cols-2 gap-6">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="rounded-2xl border border-red-500/20 bg-red-500/5 p-8">
            <div className="font-mono text-[11px] uppercase tracking-widest text-red-400 mb-4">/// WITHOUT REP FLEET</div>
            <ul className="space-y-3">
              {without.map((w) => (
                <li key={w} className="flex items-center gap-3 text-white/70">
                  <X size={16} className="text-red-400 flex-shrink-0" />{w}
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-8">
            <div className="font-mono text-[11px] uppercase tracking-widest text-emerald-400 mb-4">/// WITH REP FLEET</div>
            <ul className="space-y-3">
              {withRep.map((w) => (
                <li key={w} className="flex items-center gap-3 text-white">
                  <CheckCircle2 size={16} className="text-emerald-400 flex-shrink-0" />{w}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function GallerySection() {
  const tabs = [
    { id: "dashboard", label: "Dashboard", Mock: DashboardMock },
    { id: "workorders", label: "Work Orders", Mock: WorkOrderMock },
    { id: "inventory", label: "Inventory", Mock: InventoryMock },
    { id: "profile", label: "Vehicle Profile", Mock: VehicleProfileMock },
  ];
  const [active, setActive] = useState(tabs[0].id);
  const activeTab = tabs.find((t) => t.id === active);
  return (
    <section className="py-32 border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div {...fadeUp} className="text-center">
          <SectionEyebrow>Screenshots</SectionEyebrow>
          <h2 className="mt-4 font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tight text-white leading-tight">
            Explore REP Fleet.
          </h2>
        </motion.div>
        <div className="mt-10 flex flex-wrap justify-center gap-2">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              className={`px-4 py-2 rounded-full text-[13px] font-semibold transition-colors ${active === t.id ? "bg-white/10 text-white border border-white/20" : "text-white/60 border border-white/10 hover:text-white"}`}
              data-testid={`gallery-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <motion.div key={active} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-10 max-w-5xl mx-auto">
          <BrowserFrame><activeTab.Mock /></BrowserFrame>
        </motion.div>
      </div>
    </section>
  );
}

function FinalCtaSection() {
  return (
    <section id="contact" className="py-32 border-t border-white/5 relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: "linear-gradient(rgba(0,87,184,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,87,184,0.5) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] rounded-full bg-[#0057B8]/20 blur-[120px]" />
      </div>
      <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
        <motion.div {...fadeUp}>
          <img src="/logo.png" alt="Rep Fleet" className="w-16 h-16 mx-auto rounded-lg bg-white/95 p-1 object-contain shadow-[0_0_60px_rgba(0,87,184,0.5)]" />
          <h2 className="mt-8 font-display font-black text-5xl md:text-6xl lg:text-7xl tracking-tight text-white leading-[1.02]">
            Less downtime.<br /><span className="text-[#0057B8]">More control.</span>
          </h2>
          <p className="mt-6 text-white/60 text-lg max-w-2xl mx-auto">
            Bring your fleet maintenance operation into one connected platform.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <a href="mailto:support@rep-fleet.com" data-testid="final-cta-demo" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#0057B8] hover:bg-[#0064d1] text-white font-semibold transition-colors shadow-[0_0_40px_rgba(0,87,184,0.5)]">
              Request a Demo <ArrowRight size={16} />
            </a>
            <a href="tel:+16478396413" data-testid="final-cta-contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold transition-colors">
              Contact REP Fleet
            </a>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-white/40 font-mono">
            <a href="mailto:support@rep-fleet.com" className="hover:text-white/70 transition-colors">support@rep-fleet.com</a>
            <span className="text-white/20">·</span>
            <a href="tel:+16478396413" className="hover:text-white/70 transition-colors">+1 (647) 839-6413</a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/5 py-10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/40">
        <div className="flex items-center gap-2.5">
          <img src="/logo.png" alt="Rep Fleet" className="w-6 h-6 rounded bg-white/95 p-0.5 object-contain" />
          <span className="font-mono">© REP FLEET {new Date().getFullYear()}</span>
        </div>
        <div className="flex gap-6 font-mono text-xs">
          <Link to="/login" className="hover:text-white transition-colors">Sign in</Link>
          <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy</Link>
          <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">Terms</Link>
        </div>
      </div>
    </footer>
  );
}

// ---------- Root ----------
export default function MarketingLanding() {
  const { user } = useAuth();
  const ctaHref = user ? (user.role === "super_admin" ? "/super-admin" : "/app") : "/login";
  const ctaLabel = user ? "Open Dashboard" : "Sign in";

  return (
    <div className="min-h-screen bg-[#050505] text-white antialiased selection:bg-[#0057B8] selection:text-white" data-testid="marketing-landing">
      <Nav ctaHref={ctaHref} ctaLabel={ctaLabel} />
      <HeroSection ctaHref={ctaHref} />
      <ProductOverviewSection />
      <DriverReportingSection />
      <WorkOrdersSection />
      <PreventiveMaintenanceSection />
      <IntegrationsSection />
      <InventorySection />
      <VehicleHistorySection />
      <FeatureShowcaseSection />
      <InteractiveFleetSection />
      <RoiSection />
      <GallerySection />
      <FinalCtaSection />
      <Footer />
    </div>
  );
}
